import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styles from './TabConfiguration.module.css';
import MetaHead from '../components/MetaHead';
import { RESTO_TYPE_OPTIONS } from '../constants/restoTypes';
import { SUPPORTED_LANGS, LANG_LABELS } from '../i18n/config';
import { writeLanguageChoice } from '../i18n/detectLang';
import { API_BASE } from '../config/api';

/* ============================================================
   TabConfiguration — onboarding wizard 4 étapes
   ------------------------------------------------------------
   IDs DOM préservés (backend Express) :
     #wizard-progress       · barre de progression
     #wizard-step-1 ... #wizard-step-4 · panneaux d'étape
     #config-resto-name · #config-resto-address · #config-resto-phone
     #config-resto-type · #config-gmb-place-id  · #config-resto-website
     #config-objectif-cible · #config-objectif-delai
     #wizard-prev · #wizard-next · #wizard-finish

   Callbacks :
     onComplete(payload)    ← finaliserOnboarding()
     onStepChange(index)    ← goToStep(index)
     onValidatePlaceId(id)  ← validerPlaceId()

   i18n P5b :
     - useTranslation('tabs') + MetaHead page="configuration"
     - Validation `restoName non vide` + `address non vide` reste IDENTIQUE
       (logique inchangée), seuls les libellés sont traduits.
     - Messages d'erreur RGPD modal traduits.
     - whatsapp_message template : note "V1 = FR uniquement" affichée en
       hint à côté du champ (cf brief P5 §10 piège 5).
   ============================================================ */

// Liste des étapes : labels traduits dans le composant (cf STEPS useMemo).

// Capitalise la 1re lettre (utilisé pour le toast d'auto-fill)
function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Style inline du highlight 1.5s : bordure verte douce + halo léger.
const HIGHLIGHT_STYLE = {
  borderColor: '#5fb27a',
  boxShadow: '0 0 0 3px rgba(95, 178, 122, 0.18)',
  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
};

/* ============================================================
   ProgressBar
============================================================ */
function ProgressBar({ current, total, steps }) {
  return (
    <div id="wizard-progress" className={styles.progressWrap}>
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${((current + 1) / total) * 100}%` }}
        />
      </div>
      <div className={styles.progressDots}>
        {steps.map((s, i) => (
          <div
            key={s.id}
            className={`${styles.progressDot} ${
              i < current ? styles.progressDotDone : ''
            } ${i === current ? styles.progressDotActive : ''}`}
          >
            <span className={styles.dotInner}>
              {i < current ? '✓' : (i + 1)}
            </span>
            <span className={styles.dotLabel}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   Step1Resto
============================================================ */
function Step1Resto({ data, update, highlightedFields, t }) {
  const hl = (key) => (highlightedFields?.has(key) ? HIGHLIGHT_STYLE : undefined);
  return (
    <div id="wizard-step-1" className={styles.stepPanel}>
      <header className={styles.stepHeader}>
        <span className={styles.stepEmoji}>🏠</span>
        <div>
          <h2>{t('configuration.step1_title')}</h2>
          <p>{t('configuration.step1_sub')}</p>
        </div>
      </header>

      <div className={styles.fieldGrid}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('configuration.field_resto_name_label')} <span className={styles.req}>*</span></span>
          <input
            id="ob-nom"
            data-config-resto-name
            type="text"
            className={styles.input}
            value={data.restoName}
            onChange={(e) => update('restoName', e.target.value)}
            placeholder={t('configuration.field_resto_name_placeholder')}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('configuration.field_ville_label')} <span className={styles.req}>*</span></span>
          <input
            id="ob-ville"
            type="text"
            className={styles.input}
            value={data.ville}
            onChange={(e) => update('ville', e.target.value)}
            placeholder={t('configuration.field_ville_placeholder')}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('configuration.field_whatsapp_label')}</span>
          <input
            id="ob-whatsapp"
            type="tel"
            className={styles.input}
            value={data.whatsapp}
            onChange={(e) => update('whatsapp', e.target.value)}
            placeholder={t('configuration.field_whatsapp_placeholder')}
          />
          <span style={{ fontSize: 11, color: '#9A9890', marginTop: 4, display: 'block' }}>
            {t('configuration.field_whatsapp_hint_fr_only')}
          </span>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('configuration.field_type_label')}</span>
          <select
            id="config-resto-type"
            className={styles.select}
            value={data.type}
            onChange={(e) => update('type', e.target.value)}
          >
            <option value="">{t('configuration.field_type_placeholder')}</option>
            {RESTO_TYPE_OPTIONS.map((typ) => (
              <option key={typ.value} value={typ.value}>{typ.label}</option>
            ))}
          </select>
        </label>

        <label className={`${styles.field} ${styles.fieldFull}`}>
          <span className={styles.fieldLabel}>
            {t('configuration.field_address_label')} <span className={styles.req}>*</span>
            {highlightedFields?.has('address') && (
              <span style={{ color: '#5fb27a', marginLeft: 6, fontSize: 12 }}>{t('configuration.tag_google_auto')}</span>
            )}
          </span>
          <input
            id="config-resto-address"
            type="text"
            className={styles.input}
            style={hl('address')}
            value={data.address}
            onChange={(e) => update('address', e.target.value)}
            placeholder={t('configuration.field_address_placeholder')}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {t('configuration.field_phone_label')}
            {highlightedFields?.has('phone') && (
              <span style={{ color: '#5fb27a', marginLeft: 6, fontSize: 12 }}>{t('configuration.tag_google_auto')}</span>
            )}
          </span>
          <input
            id="config-resto-phone"
            type="tel"
            className={styles.input}
            style={hl('phone')}
            value={data.phone}
            onChange={(e) => update('phone', e.target.value)}
            placeholder={t('configuration.field_phone_placeholder')}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>
            {t('configuration.field_website_label')}
            {highlightedFields?.has('website') && (
              <span style={{ color: '#5fb27a', marginLeft: 6, fontSize: 12 }}>{t('configuration.tag_google_auto')}</span>
            )}
          </span>
          <input
            id="config-resto-website"
            type="url"
            className={styles.input}
            style={hl('website')}
            value={data.website}
            onChange={(e) => update('website', e.target.value)}
            placeholder={t('configuration.field_website_placeholder')}
          />
        </label>
      </div>
    </div>
  );
}

/* ============================================================
   Step2GMB
============================================================ */
function Step2GMB({ data, update, onValidatePlaceId, onFetchPlaceDetails, onAutofill, onAutofillFields, t }) {
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(null);
  const sync = data.googleSync;

  const handleValidate = useCallback(async () => {
    if (!data.placeId || !onValidatePlaceId) return;
    setValidating(true);
    try {
      const ok = await onValidatePlaceId(data.placeId);
      setValidated(!!ok);
      if (ok && onFetchPlaceDetails) {
        const details = await onFetchPlaceDetails(data.placeId);
        const filledKeys = [];
        const updatedKeys = [];
        if (details?.address && details.address.trim()) {
          update('address', details.address);
          filledKeys.push('address');
          updatedKeys.push('address');
        }
        if (details?.phone && details.phone.trim()) {
          update('phone', details.phone);
          filledKeys.push('phone');
          updatedKeys.push('phone');
        }
        if (details?.website && details.website.trim()) {
          update('website', details.website);
          filledKeys.push('website');
          updatedKeys.push('website');
        }
        if (onAutofill) {
          const labelMap = {
            address: t('configuration.autofill_field_address'),
            phone:   t('configuration.autofill_field_phone'),
            website: t('configuration.autofill_field_website'),
          };
          if (filledKeys.length === 3) {
            onAutofill(t('configuration.autofill_all3'));
          } else if (filledKeys.length === 2) {
            onAutofill(t('configuration.autofill_two', {
              first: capitalize(labelMap[filledKeys[0]]),
              second: labelMap[filledKeys[1]],
            }));
          } else if (filledKeys.length === 1) {
            onAutofill(t('configuration.autofill_one', { field: capitalize(labelMap[filledKeys[0]]) }));
          } else {
            onAutofill(t('configuration.autofill_none'));
          }
        }
        if (updatedKeys.length > 0 && onAutofillFields) {
          onAutofillFields(updatedKeys);
        }
      }
    } catch {
      setValidated(false);
    } finally {
      setValidating(false);
    }
  }, [data.placeId, onValidatePlaceId, onFetchPlaceDetails, onAutofill, onAutofillFields, update, t]);

  return (
    <div id="wizard-step-2" className={styles.stepPanel}>
      <header className={styles.stepHeader}>
        <span className={styles.stepEmoji}>🔗</span>
        <div>
          <h2>{t('configuration.step2_title')}</h2>
          <p>{t('configuration.step2_sub')}</p>
        </div>
      </header>

      <div id="ob-google-sync-banner" className={styles.gmbSync} style={!sync ? { display: 'none' } : undefined}>
        <div id="ob-google-sync-info" className={styles.gmbSyncInfo}>
          {t('configuration.gmb_sync_banner')}
        </div>
        <div className={styles.gmbSyncStats}>
          <div className={styles.gmbSyncStat}>
            <div className={styles.gmbSyncLabel}>{t('configuration.gmb_sync_note')} <span id="ob-note-auto-tag" className={styles.autoTag}>{t('configuration.gmb_auto_tag')}</span></div>
            <div id="ob-note" className={styles.gmbSyncValue}>{sync?.note ?? '—'}</div>
          </div>
          <div className={styles.gmbSyncStat}>
            <div className={styles.gmbSyncLabel}>{t('configuration.gmb_sync_avis')} <span id="ob-avis-auto-tag" className={styles.autoTag}>{t('configuration.gmb_auto_tag')}</span></div>
            <div id="ob-avis" className={styles.gmbSyncValue}>{sync?.avis ?? '—'}</div>
          </div>
          <div className={styles.gmbSyncStat}>
            <div className={styles.gmbSyncLabel}>{t('configuration.gmb_sync_rps')}</div>
            <div id="ob-rps" className={styles.gmbSyncValue}>{sync?.rps ?? '—'}</div>
            <div id="ob-rps-sub" className={styles.gmbSyncSub}>{sync?.rpsSub ?? ''}</div>
          </div>
        </div>
      </div>

      <div className={styles.gmbHelp}>
        <strong>{t('configuration.gmb_help_title')}</strong>
        <ol>
          <li>
            {t('configuration.gmb_help_step1_pre')}{' '}
            <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer">{t('configuration.gmb_help_step1_link')}</a>
          </li>
          <li>
            {t('configuration.gmb_help_step2_pre')} <code>?cid=</code> {t('configuration.gmb_help_step2_or')}{' '}
            <a href="https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder" target="_blank" rel="noopener noreferrer">{t('configuration.gmb_help_step2_link')}</a>
          </li>
        </ol>
      </div>

      <div className={styles.fieldGrid}>
        <label className={`${styles.field} ${styles.fieldFull}`}>
          <span className={styles.fieldLabel}>{t('configuration.field_google_url_label')}</span>
          <input
            id="ob-google-url"
            type="url"
            className={styles.input}
            value={data.googleUrl}
            onChange={(e) => update('googleUrl', e.target.value)}
            placeholder={t('configuration.field_google_url_placeholder')}
          />
        </label>

        <label className={`${styles.field} ${styles.fieldFull}`}>
          <span className={styles.fieldLabel}>{t('configuration.field_place_id_label')} <span className={styles.req}>*</span></span>
          <div className={styles.inputWithBtn}>
            <input
              id="ob-google-place-id"
              data-config-gmb-place-id
              type="text"
              className={styles.input}
              value={data.placeId}
              onChange={(e) => { update('placeId', e.target.value); setValidated(null); }}
              placeholder={t('configuration.field_place_id_placeholder')}
            />
            <button
              type="button"
              className={styles.btnGhost}
              onClick={handleValidate}
              disabled={!data.placeId || validating}
            >
              {validating ? t('configuration.btn_verifying') : t('configuration.btn_verify')}
            </button>
          </div>
          {validated === true && (
            <span className={styles.validOk}>{t('configuration.place_id_ok')}</span>
          )}
          {validated === false && (
            <span className={styles.validKo}>{t('configuration.place_id_ko')}</span>
          )}
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('configuration.field_ticket_label')}</span>
          <input
            id="ob-ticket"
            type="number"
            min="0"
            className={styles.input}
            value={data.ticket}
            onChange={(e) => update('ticket', e.target.value)}
            placeholder={t('configuration.field_ticket_placeholder')}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('configuration.field_couverts_label')}</span>
          <input
            id="ob-couverts"
            type="number"
            min="0"
            className={styles.input}
            value={data.couverts}
            onChange={(e) => update('couverts', e.target.value)}
            placeholder={t('configuration.field_couverts_placeholder')}
          />
        </label>
      </div>

      <div className={styles.skipNote}>
        <em>{t('configuration.skip_note')}</em>
      </div>
    </div>
  );
}

/* ============================================================
   Step3Objectif
============================================================ */
function Step3Objectif({ data, update, t }) {
  const cibles = [
    { value: 4.0, label: t('configuration.cible_40_label'), desc: t('configuration.cible_40_desc') },
    { value: 4.3, label: t('configuration.cible_43_label'), desc: t('configuration.cible_43_desc') },
    { value: 4.5, label: t('configuration.cible_45_label'), desc: t('configuration.cible_45_desc') },
    { value: 4.7, label: t('configuration.cible_47_label'), desc: t('configuration.cible_47_desc') },
  ];

  return (
    <div id="wizard-step-3" className={styles.stepPanel}>
      <header className={styles.stepHeader}>
        <span className={styles.stepEmoji}>🎯</span>
        <div>
          <h2>{t('configuration.step3_title')}</h2>
          <p>{t('configuration.step3_sub')}</p>
        </div>
      </header>

      <div className={styles.cibleGrid}>
        {cibles.map((c) => (
          <button
            key={c.value}
            type="button"
            id={c.value === 4.5 ? 'config-objectif-cible' : undefined}
            className={`${styles.cibleCard} ${
              data.cible === c.value ? styles.cibleCardActive : ''
            }`}
            onClick={() => update('cible', c.value)}
          >
            <div className={styles.cibleStars}>
              {'★'.repeat(Math.floor(c.value))}
              <span className={styles.cibleStarsDim}>{'★'.repeat(5 - Math.floor(c.value))}</span>
            </div>
            <div className={styles.cibleLabel}>{c.label}</div>
            <div className={styles.cibleDesc}>{c.desc}</div>
          </button>
        ))}
      </div>

      <div className={styles.fieldGrid}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('configuration.field_horizon_label')}</span>
          <select
            id="ob-horizon-objectif"
            data-config-objectif-delai
            className={styles.select}
            value={data.horizonObjectif}
            onChange={(e) => update('horizonObjectif', e.target.value)}
          >
            <option value="3m">{t('configuration.horizon_3m')}</option>
            <option value="6m">{t('configuration.horizon_6m')}</option>
            <option value="12m">{t('configuration.horizon_12m')}</option>
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('configuration.field_objectif_avis_label')}</span>
          <input
            id="ob-objectif-avis"
            type="number"
            min="0"
            className={styles.input}
            value={data.objectifAvis}
            onChange={(e) => update('objectifAvis', e.target.value)}
            placeholder={t('configuration.field_objectif_avis_placeholder')}
          />
        </label>
      </div>
    </div>
  );
}

/* ============================================================
   Step4Review
============================================================ */
function Step4Review({ data, t }) {
  const typeLabel = RESTO_TYPE_OPTIONS.find(opt => opt.value === data.type)?.label || data.type;
  const horizonMap = {
    3:  t('configuration.horizon_3m'),
    6:  t('configuration.horizon_6m'),
    12: t('configuration.horizon_12m'),
  };
  return (
    <div id="wizard-step-summary" className={styles.stepPanel}>
      <header className={styles.stepHeader}>
        <span className={styles.stepEmoji}>✓</span>
        <div>
          <h2>{t('configuration.step4_title')}</h2>
          <p>{t('configuration.step4_sub')}</p>
        </div>
      </header>

      <div className={styles.summary}>
        <div className={styles.summaryGroup}>
          <div className={styles.summaryGroupTitle}>{t('configuration.summary_resto_title')}</div>
          <dl>
            <div><dt>{t('configuration.summary_resto_name')}</dt><dd id="sum-nom">{data.restoName || <em>—</em>}</dd></div>
            <div><dt>{t('configuration.summary_resto_type')}</dt><dd>{typeLabel || <em>—</em>}</dd></div>
            <div><dt>{t('configuration.summary_resto_ville')}</dt><dd id="sum-ville">{data.ville || <em>—</em>}</dd></div>
            <div><dt>{t('configuration.summary_resto_address')}</dt><dd>{data.address || <em>—</em>}</dd></div>
            <div><dt>{t('configuration.summary_resto_phone')}</dt><dd>{data.phone || <em>—</em>}</dd></div>
            <div><dt>{t('configuration.summary_resto_couverts')}</dt><dd id="sum-couverts">{data.couverts || <em>—</em>}</dd></div>
            <div><dt>{t('configuration.summary_resto_ticket')}</dt><dd id="sum-ticket">{data.ticket ? t('configuration.summary_ticket_value', { value: data.ticket }) : <em>—</em>}</dd></div>
          </dl>
        </div>

        <div id="gmb-status-block" className={styles.summaryGroup}>
          <div className={styles.summaryGroupTitle}>{t('configuration.summary_gmb_title')}</div>
          <dl>
            <div><dt>{t('configuration.summary_gmb_place')}</dt>
              <dd id="sum-google" className={styles.mono}>
                {data.placeId ? `${data.placeId.slice(0, 24)}…` : <em>{t('configuration.summary_gmb_empty')}</em>}
              </dd>
            </div>
          </dl>
        </div>

        <div className={styles.summaryGroup}>
          <div className={styles.summaryGroupTitle}>{t('configuration.summary_objectif_title')}</div>
          <dl>
            <div><dt>{t('configuration.summary_objectif_cible')}</dt>
              <dd><strong className={styles.mono}>{data.cible.toFixed(1)} ★</strong></dd>
            </div>
            <div><dt>{t('configuration.summary_objectif_horizon')}</dt>
              <dd>{horizonMap[parseInt(data.horizonObjectif)] || data.horizonObjectif}</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className={styles.finishHint}>
        <span>🌿</span>
        <p>
          <Trans
            i18nKey="configuration.finish_hint"
            ns="tabs"
            components={[<strong key="b" />]}
          />
        </p>
      </div>

      {/* ===== Préférences (i18n P6) ===== */}
      <PreferencesSection t={t} />

      {/* ===== Données & RGPD ===== */}
      <RGPDSection restoName={data.restoName} t={t} />
    </div>
  );
}

/* ============================================================
   PreferencesSection (i18n Phase 6)
   ------------------------------------------------------------
   Section "Préférences" — actuellement contient uniquement la
   langue préférée. Pourrait s'étendre plus tard (timezone, etc.).

   Le onChange :
     1) Met à jour i18next runtime (i18n.changeLanguage)
     2) Persiste le choix local (writeLanguageChoice → localStorage)
     3) Persiste backend best-effort (PATCH /api/account/preferred-lang)
        utilisé par weekly-report + insightsService
============================================================ */
function PreferencesSection({ t }) {
  const { i18n } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const currentLang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : 'fr';

  const handleChange = useCallback(async (e) => {
    const newLang = e.target.value;
    if (!SUPPORTED_LANGS.includes(newLang) || newLang === currentLang) return;

    setSaving(true);
    setSavedMsg('');

    // 1) Update UI immédiatement (i18next + localStorage)
    writeLanguageChoice(newLang);
    await i18n.changeLanguage(newLang);

    // 2) Persistance backend best-effort. Silent fail si offline / 500.
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${API_BASE}/api/account/preferred-lang`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ lang: newLang }),
        });
      }
    } catch {
      // Ne bloque pas l'UX local
    }

    setSaving(false);
    setSavedMsg(t('configuration.preferred_lang_saved'));
    // Auto-hide après 2.5s
    setTimeout(() => setSavedMsg(''), 2500);
  }, [currentLang, i18n, t]);

  return (
    <section className={styles.rgpdSection}>
      <h3 className={styles.rgpdTitle}>{t('configuration.preferences_title')}</h3>
      <p className={styles.rgpdLead}>{t('configuration.preferences_lead')}</p>

      <div className={styles.rgpdRow}>
        <div className={styles.rgpdAction}>
          <div>
            <div className={styles.rgpdActionTitle}>{t('configuration.preferred_lang_label')}</div>
            <div className={styles.rgpdActionDesc}>{t('configuration.preferred_lang_hint')}</div>
          </div>
          <select
            id="preferred-lang-select"
            className={styles.input}
            value={currentLang}
            onChange={handleChange}
            disabled={saving}
            style={{ maxWidth: 200 }}
          >
            {SUPPORTED_LANGS.map((lng) => {
              const meta = LANG_LABELS[lng];
              return (
                <option key={lng} value={lng}>
                  {meta.flag} {meta.native}
                </option>
              );
            })}
          </select>
        </div>
        {savedMsg && (
          <div className={styles.rgpdFeedback} role="status" aria-live="polite">
            {savedMsg}
          </div>
        )}
      </div>
    </section>
  );
}

/* ============================================================
   RGPDSection — export de données + suppression de compte
============================================================ */
function RGPDSection({ restoName, t }) {
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportMsg('');
    try {
      if (typeof window !== 'undefined' && window.Revyo?.exportData) {
        await window.Revyo.exportData();
      }
      setExportMsg(t('configuration.rgpd_export_ok'));
    } catch {
      setExportMsg(t('configuration.rgpd_export_ko'));
    } finally {
      setExporting(false);
    }
  }, [t]);

  const handleConfirmDelete = useCallback(async () => {
    setErrorMsg('');
    if (confirmName.trim().toLowerCase() !== (restoName || '').trim().toLowerCase()) {
      setErrorMsg(t('configuration.rgpd_modal_err_name'));
      return;
    }
    setDeleting(true);
    try {
      if (typeof window !== 'undefined' && window.Revyo?.deleteAccount) {
        await window.Revyo.deleteAccount(confirmName);
      }
    } catch {
      setErrorMsg(t('configuration.rgpd_modal_err_delete'));
    } finally {
      setDeleting(false);
    }
  }, [confirmName, restoName, t]);

  return (
    <section className={styles.rgpdSection}>
      <h3 className={styles.rgpdTitle}>{t('configuration.rgpd_title')}</h3>
      <p className={styles.rgpdLead}>{t('configuration.rgpd_lead')}</p>

      <div className={styles.rgpdRow}>
        <div className={styles.rgpdAction}>
          <div>
            <div className={styles.rgpdActionTitle} id="btn-export-label">{t('configuration.rgpd_export_title')}</div>
            <div className={styles.rgpdActionDesc}>{t('configuration.rgpd_export_desc')}</div>
          </div>
          <button
            id="btn-export-data"
            type="button"
            className={styles.btnGhost}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? t('configuration.rgpd_btn_exporting') : t('configuration.rgpd_btn_export')}
          </button>
        </div>
        {exportMsg && <div id="export-feedback" className={styles.rgpdFeedback}>{exportMsg}</div>}

        <div className={`${styles.rgpdAction} ${styles.rgpdDanger}`}>
          <div>
            <div className={styles.rgpdActionTitle}>{t('configuration.rgpd_delete_title')}</div>
            <div className={styles.rgpdActionDesc}>{t('configuration.rgpd_delete_desc')}</div>
          </div>
          <button
            id="btn-delete-account"
            type="button"
            className={styles.btnDanger}
            onClick={() => { setModalOpen(true); setConfirmName(''); setErrorMsg(''); }}
          >
            {t('configuration.rgpd_btn_delete')}
          </button>
        </div>
      </div>

      {modalOpen && (
        <div id="delete-account-modal" className={styles.modal} role="dialog" aria-modal="true">
          <div className={styles.modalCard}>
            <h4 id="delete-account-title" className={styles.modalTitle}>{t('configuration.rgpd_modal_title')}</h4>
            <p className={styles.modalLead}>
              <Trans
                i18nKey="configuration.rgpd_modal_lead"
                ns="tabs"
                values={{ name: restoName || '—' }}
                components={[<strong key="b0" />, <strong key="b1" />]}
              />
            </p>
            <p id="delete-confirm-name-hint" className={styles.modalHint}>
              {t('configuration.rgpd_modal_hint_pre')} <code>{restoName || '—'}</code>
            </p>
            <input
              id="delete-confirm-input"
              type="text"
              className={styles.input}
              value={confirmName}
              onChange={(e) => { setConfirmName(e.target.value); setErrorMsg(''); }}
              placeholder={restoName || t('configuration.rgpd_modal_input_placeholder')}
            />
            {errorMsg && <div id="delete-error-msg" className={styles.modalError}>{errorMsg}</div>}
            <div className={styles.modalActions}>
              <button type="button" className={styles.btnGhost} onClick={() => setModalOpen(false)}>{t('configuration.rgpd_btn_cancel')}</button>
              <button
                id="btn-confirm-delete"
                type="button"
                className={styles.btnDanger}
                onClick={handleConfirmDelete}
                disabled={!confirmName.trim() || deleting}
              >
                {deleting ? t('configuration.rgpd_btn_deleting') : t('configuration.rgpd_btn_confirm_delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ============================================================
   TabConfiguration
============================================================ */
export default function TabConfiguration({
  initialData = {},
  onComplete,
  onStepChange,
  onValidatePlaceId,
  onFetchPlaceDetails,
}) {
  const { t } = useTranslation('tabs');

  const STEPS = useMemo(() => [
    { id: 'resto',    label: t('configuration.step_resto_label'),    desc: t('configuration.step_resto_desc') },
    { id: 'gmb',      label: t('configuration.step_gmb_label'),      desc: t('configuration.step_gmb_desc') },
    { id: 'objectif', label: t('configuration.step_objectif_label'), desc: t('configuration.step_objectif_desc') },
    { id: 'review',   label: t('configuration.step_review_label'),   desc: t('configuration.step_review_desc') },
  ], [t]);

  const [step, setStep] = useState(0);
  const [autofillToast, setAutofillToast] = useState('');
  const handleAutofill = useCallback((msg) => {
    setAutofillToast(msg);
    setTimeout(() => setAutofillToast(''), 3500);
  }, []);
  const [highlightedFields, setHighlightedFields] = useState(() => new Set());
  const handleAutofillFields = useCallback((keys) => {
    setHighlightedFields(new Set(keys));
    setTimeout(() => setHighlightedFields(new Set()), 1500);
  }, []);
  const [data, setData] = useState({
    restoName: initialData.restoName ?? '',
    type:      initialData.type ?? '',
    address:   initialData.address ?? '',
    ville:     initialData.ville ?? '',
    whatsapp:  initialData.whatsapp ?? '',
    phone:     initialData.phone ?? '',
    website:   initialData.website ?? '',
    placeId:   initialData.placeId ?? '',
    googleUrl: initialData.googleUrl ?? '',
    ticket:    initialData.ticket ?? '',
    couverts:  initialData.couverts ?? '',
    cible:     initialData.cible ?? 4.5,
    objectifAvis: initialData.objectifAvis ?? 30,
    horizonObjectif: initialData.horizonObjectif ?? '6m',
    googleSync: initialData.googleSync ?? null,
  });
  const [submitting, setSubmitting] = useState(false);

  const update = useCallback((k, v) => {
    setData((prev) => ({ ...prev, [k]: v }));
  }, []);

  const canNext = useMemo(() => {
    if (step === 0) return data.restoName.trim() && data.address.trim();
    if (step === 1) return data.placeId.trim().length >= 5;
    if (step === 2) return !!data.cible;
    return true;
  }, [step, data]);

  const goTo = useCallback((i) => {
    setStep(i);
    onStepChange && onStepChange(i);
  }, [onStepChange]);

  const next = useCallback(() => {
    if (step < STEPS.length - 1) goTo(step + 1);
  }, [step, goTo, STEPS.length]);

  const prev = useCallback(() => {
    if (step > 0) goTo(step - 1);
  }, [step, goTo]);

  const finish = useCallback(async () => {
    if (!onComplete) return;
    setSubmitting(true);
    try { await onComplete(data); }
    finally { setSubmitting(false); }
  }, [data, onComplete]);

  return (
    <>
      <MetaHead page="configuration" />
      <header className={styles.tabHeader}>
        <div className={styles.titleBlock}>
          <h1><span className={styles.titleEmoji}>{t('configuration.title_emoji')}</span>{t('configuration.title')}</h1>
          <p>{t('configuration.subtitle')}</p>
        </div>
        <div className={styles.actions}>
          <span className={styles.stepCount}>{t('configuration.step_counter', { current: step + 1, total: STEPS.length })}</span>
        </div>
      </header>

      <div className={styles.wizard}>
        <ProgressBar current={step} total={STEPS.length} steps={STEPS} />

        {autofillToast && (
          <div
            role="status"
            aria-live="polite"
            style={{
              margin: '8px 0 0',
              padding: '10px 14px',
              background: 'rgba(232, 80, 42, 0.08)',
              color: '#7a3a1f',
              border: '1px solid rgba(232, 80, 42, 0.25)',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {autofillToast}
          </div>
        )}

        <div className={styles.wizardBody}>
          {step === 0 && <Step1Resto    data={data} update={update} highlightedFields={highlightedFields} t={t} />}
          {step === 1 && <Step2GMB      data={data} update={update} onValidatePlaceId={onValidatePlaceId} onFetchPlaceDetails={onFetchPlaceDetails} onAutofill={handleAutofill} onAutofillFields={handleAutofillFields} t={t} />}
          {step === 2 && <Step3Objectif data={data} update={update} t={t} />}
          {step === 3 && <Step4Review   data={data} t={t} />}
        </div>

        <footer className={styles.wizardFooter}>
          <button
            id="wizard-prev"
            type="button"
            className={styles.btnGhost}
            onClick={prev}
            disabled={step === 0}
          >
            {t('configuration.btn_prev')}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              id="wizard-next"
              type="button"
              className={styles.btnPrimary}
              onClick={next}
              disabled={!canNext}
            >
              {t('configuration.btn_next')}
            </button>
          ) : (
            <button
              id="wizard-finish"
              type="button"
              className={styles.btnPrimary}
              onClick={finish}
              disabled={submitting}
            >
              {submitting ? t('configuration.btn_finishing') : t('configuration.btn_finish')}
            </button>
          )}
        </footer>
      </div>
    </>
  );
}
